import { useContext, useMemo } from 'react';
import CbmContext from '../Context/Cbm/cbmContext';

/**
 * Valid authority levels in the system
 * @type {readonly ['Admin', 'Office', 'DM', 'AS']}
 */
export const AUTHORITIES = Object.freeze(['Admin', 'Office', 'DM', 'AS']);

/**
 * Permission levels for feature access
 * @typedef {'Admin' | 'Office' | 'DM' | 'AS'} Authority
 */

/**
 * Hook to check user permissions across the admin module
 * Centralizes permission checking logic that was previously duplicated in 6+ files
 *
 * @returns {Object} Permission object with boolean flags and utility methods
 *
 * @example
 * const { isAdmin, hasOfficeAccess, canManageUsers } = usePermissions();
 * if (canManageUsers) {
 *   // Show user management UI
 * }
 */
const usePermissions = () => {
  const cbmContext = useContext(CbmContext);
  const { portalUser } = cbmContext || {};

  return useMemo(() => {
    // Get primary authority from authorities array
    const authority = portalUser?.authorities?.[0]?.authority || null;

    // Also check the legacy permission field as fallback
    const legacyPermission = portalUser?.permission || null;
    const effectiveAuthority = authority || legacyPermission;

    // Core permission flags
    const isAdmin = effectiveAuthority === 'Admin';
    const isOffice = effectiveAuthority === 'Office';
    const isDM = effectiveAuthority === 'DM';
    const isAS = effectiveAuthority === 'AS';

    // Access level flags (cumulative permissions)
    const hasAdminAccess = isAdmin;
    const hasOfficeAccess = isAdmin || isOffice;
    const hasDMAccess = isAdmin || isOffice || isDM;
    const hasAnyAccess = hasDMAccess || isAS;

    // Feature-specific permissions
    const canManageUsers = isAdmin;
    const canManageSessions = isAdmin;
    const canViewAllStores = hasOfficeAccess;
    const canViewAllForms = hasOfficeAccess || isDM || isAS;
    const canManageDistricts = isAdmin || isOffice;
    const canManageSubAreas = isAdmin || isOffice;
    const canDeleteUsers = isAdmin;
    const canAssignAuthorities = isAdmin;

    // Get all authorities (for multi-role users)
    const allAuthorities =
      portalUser?.authorities?.map((a) => a.authority).filter(Boolean) || [];

    /**
     * Check if user has a specific authority
     * @param {Authority} auth - The authority to check
     * @returns {boolean}
     */
    const hasAuthority = (auth) => {
      return allAuthorities.includes(auth) || effectiveAuthority === auth;
    };

    /**
     * Check if user has any of the specified authorities
     * @param {Authority[]} auths - Array of authorities to check
     * @returns {boolean}
     */
    const hasAnyAuthority = (auths) => {
      return auths.some((auth) => hasAuthority(auth));
    };

    /**
     * Check if user has all of the specified authorities
     * @param {Authority[]} auths - Array of authorities to check
     * @returns {boolean}
     */
    const hasAllAuthorities = (auths) => {
      return auths.every((auth) => hasAuthority(auth));
    };

    return {
      // User info
      portalUser,
      authority: effectiveAuthority,
      allAuthorities,

      // Core permission flags
      isAdmin,
      isOffice,
      isDM,
      isAS,

      // Access level flags
      hasAdminAccess,
      hasOfficeAccess,
      hasDMAccess,
      hasAnyAccess,

      // Feature-specific permissions
      canManageUsers,
      canManageSessions,
      canViewAllStores,
      canViewAllForms,
      canManageDistricts,
      canManageSubAreas,
      canDeleteUsers,
      canAssignAuthorities,

      // Utility methods
      hasAuthority,
      hasAnyAuthority,
      hasAllAuthorities,
    };
  }, [portalUser]);
};

export default usePermissions;
